// Character sprites built from a look recipe by the townkit (render/kit.ts), cached as canvases and reused every
// frame. New sprites are built under a per-frame budget so twenty people arriving at once never causes a hitch.
import { toKit, type KitLook } from '../../../shared/kit.js';
import type { Look } from '../../../shared/look.js';
import { compose, moodOf, slash, PORTRAIT_CROP, type Mood, type Pose } from './kit.js';

export type Mode = 'i' | 'w' | 'a';
/** Walk frames in the townkit cycle. */
export const WALK_FRAMES = 6;
/** The attack: wind up, swing (two frames), then settle. Durations in ticks (60 a second). */
const ATTACK_POSES: Pose[] = ['windup', 'strike', 'strike', 'recover', 'recover'];
const ATTACK_TICKS = [5, 4, 4, 5, 8];

const VIEW: Record<string, { view: 'front' | 'back' | 'side'; mirror: boolean }> = {
  down: { view: 'front', mirror: false }, up: { view: 'back', mirror: false }, right: { view: 'side', mirror: false }, left: { view: 'side', mirror: true },
};

function toCanvas(img: ImageData, mirror: boolean): HTMLCanvasElement {
  const a = document.createElement('canvas'); a.width = img.width; a.height = img.height; a.getContext('2d')!.putImageData(img, 0, 0);
  return mirror ? mirrored(a) : a;
}

export class SpriteCache {
  private sprites = new Map<string, HTMLCanvasElement>();
  private portraits = new Map<string, HTMLCanvasElement>();
  private keys = new WeakMap<object, string>();
  private kits = new WeakMap<object, KitLook>();
  private budget = 0; private deadline = 0;
  built = 0;

  /** Each frame may build at least `min` new sprites, and more while it stays under `ms` of work. */
  frameStart(min = 4, ms = 4): void { this.budget = min; this.deadline = performance.now() + ms; }

  /** Looks from before the townkit (old saves, townsfolk written for the old engine) are converted once. */
  private kit(look: Look): KitLook { let k = this.kits.get(look); if (!k) { k = toKit(look); this.kits.set(look, k); } return k; }
  private lookKey(look: Look): string {
    let k = this.keys.get(look); if (!k) { k = JSON.stringify(this.kit(look)); this.keys.set(look, k); } return k;
  }

  /** A character frame, or null if it isn't built yet and this frame's budget is spent. */
  get(look: Look, dir: string, mode: Mode, fi: number, blink: boolean, face: string | null = null, force = false): HTMLCanvasElement | null {
    const key = this.lookKey(look) + dir + mode + fi + (blink ? 'b' : '') + (face || '');
    let cv = this.sprites.get(key);
    if (cv) { this.sprites.delete(key); this.sprites.set(key, cv); return cv; }   // most recently used last, so crowds don't evict what's on screen
    if (!force && this.budget <= 0 && performance.now() > this.deadline) return null;
    this.budget--; this.built++;
    const v = VIEW[dir] ?? VIEW.down;
    let mood: Mood = moodOf(face);
    if (blink && (mood === 'normal' || mood === 'talk')) mood = 'sleepy';
    const frame = mode === 'w' ? fi % WALK_FRAMES : null;
    const pose: Pose = mode === 'a' ? ATTACK_POSES[Math.min(fi, ATTACK_POSES.length - 1)] : 'stand';
    cv = toCanvas(compose(this.kit(look), { view: v.view, frame, mood, pose }), false);
    if (mode === 'a' && (fi === 1 || fi === 2)) slash(cv.getContext('2d')!, v.view, fi === 1);   // the swoosh as the swing lands
    if (v.mirror) cv = mirrored(cv);
    this.sprites.set(key, cv);
    if (this.sprites.size > 4000) this.sprites.delete(this.sprites.keys().next().value!);   // drop the least recently used
    return cv;
  }

  /** A conversation portrait: always facing you, face and torso, with the expression of the line. */
  portrait(look: Look, face: string): HTMLCanvasElement {
    const key = this.lookKey(look) + face;
    let cv = this.portraits.get(key);
    if (!cv) {
      const frame = toCanvas(compose(this.kit(look), { view: 'front', frame: null, mood: moodOf(face) }), false), { x, y, w, h } = PORTRAIT_CROP;
      cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      const c = cv.getContext('2d')!;
      c.fillStyle = '#f1e6cf'; c.fillRect(0, 0, w, h);
      c.fillStyle = '#f9f2e2'; c.beginPath(); c.ellipse(w / 2, h / 2 + 2, w * 0.42, h * 0.4, 0, 0, Math.PI * 2); c.fill();
      c.drawImage(frame, x, y, w, h, 0, 0, w, h);
      this.portraits.set(key, cv);
    }
    return cv;
  }
}

function mirrored(cv: HTMLCanvasElement): HTMLCanvasElement {
  const out = document.createElement('canvas'); out.width = cv.width; out.height = cv.height;
  const c = out.getContext('2d')!; c.translate(out.width, 0); c.scale(-1, 1); c.drawImage(cv, 0, 0);
  return out;
}

/** Which attack pose a tick count falls in. */
export function attackFrame(t: number): number {
  let acc = 0;
  for (let i = 0; i < ATTACK_TICKS.length; i++) { acc += ATTACK_TICKS[i]; if (t < acc) return i; }
  return ATTACK_TICKS.length - 1;
}
